import { ConditionWrapper, Form, RichEditor } from '@approvalmax/ui/src/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { constants } from 'modules/common';
import { domain } from 'modules/data';
import { useDispatch, useSelector } from 'modules/react-redux';
import { memo, useEffect } from 'react';
import bemFactory from 'react-bem-factory';
import { useForm } from 'react-hook-form';
import { useUserBetaFeatures } from 'shared/hooks';
import { z } from 'zod';

import { editRequestNote } from '../../actions';
import { getRequest, getShowValidationErrors } from '../../selectors/pageSelectors';
import { getFieldsApprovalPermissionBySystemPurpose } from '../../selectors/requestSelectors';
import { SectionContainer } from '../SectionContainer';
import { noteFieldName } from './NoteForApproversSection.constants';
import { messages } from './NoteForApproversSection.messages';
import { createSchema } from './NoteForApproversSection.schema';

const qa = bemFactory.block('reqf-request-note-section');

export const NoteForApproversSection = memo(() => {
    const dispatch = useDispatch();
    const request = useSelector(getRequest);
    const showValidationErrors = useSelector(getShowValidationErrors);
    const [noteForApproversFieldPermission] = useSelector((state) =>
        getFieldsApprovalPermissionBySystemPurpose(state, request, [domain.FieldSystemPurpose.NoteForApprovers])
    );

    const { isBelgradeUINoteForApproversSection } = useUserBetaFeatures();

    const schema = createSchema(noteForApproversFieldPermission.required);
    const form = useForm<z.infer<typeof schema>>({
        defaultValues: {
            [noteFieldName]: request.requestNote.value || '',
        },
        mode: 'onChange',
        resolver: zodResolver(schema),
    });

    const note = form.watch(noteFieldName);
    const noteError = form.formState.errors[noteFieldName];

    useEffect(() => {
        if (showValidationErrors) {
            void form.trigger();
        }
    }, [form, showValidationErrors]);

    useEffect(() => {
        if (form.formState.isDirty) {
            dispatch(
                editRequestNote({
                    value: note || '',
                    errorMessages: noteError?.message ? [noteError?.message] : null,
                })
            );
        }
    }, [dispatch, form.formState.isDirty, note, noteError?.message]);

    return (
        <ConditionWrapper
            condition={!isBelgradeUINoteForApproversSection}
            wrapperIf={(children) => (
                <SectionContainer required={noteForApproversFieldPermission.required} title={messages.label}>
                    {children}
                </SectionContainer>
            )}
        >
            <Form form={form}>
                <RichEditor
                    name={noteFieldName}
                    label={isBelgradeUINoteForApproversSection ? messages.label : undefined}
                    placeholder={messages.placeholder}
                    dataQa={qa('requestNote-editor')}
                    readOnly={noteForApproversFieldPermission.disabled}
                    disabled={noteForApproversFieldPermission.disabled}
                    required={noteForApproversFieldPermission.required}
                    maxLength={constants.commonConstants.REQUEST_NOTE_MAX_LENGTH}
                    height={isBelgradeUINoteForApproversSection ? '100%' : undefined}
                    minHeight={100}
                />
            </Form>
        </ConditionWrapper>
    );
});

NoteForApproversSection.displayName = 'NoteForApproversSection';
